import { ReactNode, useState } from 'react';
import { Spinner, Toaster, makeStyles, mergeClasses, tokens, useId } from '@fluentui/react-components';
import { MainMenu } from './';
import { Column, Row } from '../common';
import { VerticalNavMenu } from './VerticalNavMenu';
import { Login } from '../pages/Auth/Login';
import { Authenticated, Unauthenticated } from '../common/Authenticated';
import { UploadDocumentsControl } from '../common/UploadDocumentsControl';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { FloatingTray } from '../common/FloatingTray';
import { appTokens } from '../common/Styles/appTokens';

const useStyles = makeStyles({
    mainRow: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        height: 'calc(100vh)',
        width: '100%',
        columnGap: '0px',
        flexWrap: 'nowrap',
    },
    menu: {
        position: 'sticky',
        top: '0px',
        left: '0px',
        width: '100%',
        minHeight: appTokens.menuHeight,
    },
    sideMenu: {
        height: '100%',
        zIndex: '20',
        width: '260px',
        opacity: 1,
        transition: `width ${tokens.durationSlow} ease, opacity ${tokens.durationSlow} ease`,
    },
    sideMenuCollapsed: {
        width: '0px',
        opacity: 0,
        overflow: 'hidden',
        scrollbarWidth: 'none',
    },
    sideMenuRail: {
        width: appTokens.verticalMenu.railWidth,
    },
    rightCol: {
        height: '100%',
        overflow: 'hidden',
        transitionDuration: appTokens.transitionDuration,
        padding: '0rem',
        backgroundColor: appTokens.pages.background,
    },
});

type LayoutProps = {
    children: ReactNode;
    isInitializing?: boolean;
    loginError?: string;
    hasPortalAccess?: boolean;
};

export const Layout = ({ children, isInitializing = false, loginError, hasPortalAccess = false }: LayoutProps) => {
    const toasterId = useId(undefined, 'main-toast');
    const mediaSizes = useMediaSizes();
    const [isMenuOpen, setIsMenuOpen] = useState(true);
    const styles = useStyles();
    const sideNavClasses = mergeClasses(
        styles.sideMenu,
        !mediaSizes.isUnderLaptop && !isMenuOpen && styles.sideMenuRail,
        mediaSizes.isUnderLaptop && styles.sideMenuCollapsed,
    );

    return (
        <div>
            <Row className={styles.mainRow} noWrap>
                <Authenticated>
                    {isInitializing || !hasPortalAccess ? (
                        <Column width='100%' align='center' vAlign='middle'>
                            <Spinner size='large' label='Loading your workspace...' />
                        </Column>
                    ) : (
                        <>
                            <Column flex='0'>
                                <VerticalNavMenu
                                    className={sideNavClasses}
                                    open={isMenuOpen}
                                    setIsOpen={setIsMenuOpen}
                                />
                            </Column>
                            <Column noGap vAlign='top' className={styles.rightCol} flex='1'>
                                <MainMenu
                                    className={styles.menu}
                                    isSideMenuOpen={isMenuOpen && !mediaSizes.isUnderLaptop}
                                />
                                <Row noGap noWrap>
                                    <Column
                                        flex='1 1 100%'
                                        minWidth='0px'
                                        padding='px-3 py-2'
                                        height={appTokens.pageHeight()}
                                        overflowY='clip'
                                    >
                                        {children}
                                    </Column>
                                </Row>
                            </Column>
                            <FloatingTray>
                                <Row gap='medium' height='auto'>
                                    <Column flex='0 0 auto' vAlign='bottom'>
                                        <UploadDocumentsControl />
                                    </Column>
                                </Row>
                            </FloatingTray>
                        </>
                    )}
                </Authenticated>
                <Unauthenticated>
                    <Login error={loginError} />
                </Unauthenticated>
            </Row>
            <Toaster toasterId={toasterId} limit={4} position='bottom-end' />
        </div>
    );
};
